export const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

  /* ══════════════════════════════════════════════════════════════════════════
     TOKENS — editor theme. One accent (amber). Cool-tinted grays throughout.
  ══════════════════════════════════════════════════════════════════════════ */
  :root {
    --bg:         #0d0f13;   /* window backdrop  */
    --editor:     #121519;   /* editor surface   */
    --panel:      #0f1116;   /* sidebar / rails  */
    --raised:     #171b21;   /* cards            */
    --line:       #1f242b;   /* borders          */
    --line-soft:  #171b22;

    --fg:         #d6dae1;
    --fg-dim:     #8d95a1;
    --fg-mute:    #5d6672;
    --gutter:     #333a44;

    /* The only accent. Monochrome by choice — all hue in this UI comes from
       the syntax tokens below. Swapping these four lines re-themes the site. */
    --accent:     #e8ebef;
    --accent-dim: #9aa1ab;
    --accent-rgb: 232, 235, 239;   /* for rgba() washes */
    --accent-ink: #0d0f13;         /* text drawn on top of --accent */

    /* syntax — semantic, deliberately desaturated */
    --syn-key:    #b17fc4;
    --syn-str:    #9bb06e;
    --syn-fn:     #6fa3c4;
    --syn-num:    #cf8e6d;
    --syn-com:    #4e5764;

    --ok:         #7fa860;
    --err:        #cf6d6d;

    --titlebar:   34px;
    --statusbar:  26px;
    --lh:         22px;      /* the "code line" unit — gutter is locked to it */

    --hl:         rgba(255,255,255,.022);  /* current-line wash  */
    --shadow:     rgba(4,6,9,.6);          /* card lift, tinted  */
    --grain-op:   .05;

    --z-noise:    1;
    --z-content:  10;
    --z-rail:     20;
    --z-chrome:   30;
    --z-overlay:  40;
  }

  /* ══════════════════════════════════════════════════════════════════════════
     THEMES
     Each block redefines only the palette; every rule below reads tokens, and
     derived surfaces (hover, wells, skeletons) come from color-mix(), so a
     theme never has to restate them. data-theme is set on <html> — an inline
     script in index.html applies it before first paint to avoid a flash.
  ══════════════════════════════════════════════════════════════════════════ */
  :root, :root[data-theme="dark"] { color-scheme: dark; }

  /* ── Light+ ─────────────────────────────────────────────────────────────── */
  :root[data-theme="light"] {
    color-scheme: light;
    --bg: #e9ebef; --editor: #ffffff; --panel: #f4f5f7; --raised: #ffffff;
    --line: #dbdee3; --line-soft: #e9ebee;
    --fg: #1f2328; --fg-dim: #4d555f; --fg-mute: #767d87; --gutter: #b4bac2;

    --accent: #1f2328; --accent-dim: #767d87;
    --accent-rgb: 31, 35, 40; --accent-ink: #ffffff;

    --syn-key: #8a4b96; --syn-str: #3f7d33; --syn-fn: #3a6ea5;
    --syn-num: #b0562a; --syn-com: #9aa0a8;

    --ok: #3f7d33; --err: #c0392b;
    --hl: rgba(0,0,0,.032); --shadow: rgba(31,35,45,.13); --grain-op: .022;
  }

  /* ── Monokai ────────────────────────────────────────────────────────────── */
  :root[data-theme="monokai"] {
    color-scheme: dark;
    --bg: #1c1d18; --editor: #272822; --panel: #21221c; --raised: #2f302a;
    --line: #3d3e36; --line-soft: #32332c;
    --fg: #f8f8f2; --fg-dim: #b6b6ae; --fg-mute: #8a8674; --gutter: #57584f;

    --accent: #a6e22e; --accent-dim: #7fae22;
    --accent-rgb: 166, 226, 46; --accent-ink: #272822;

    --syn-key: #f92672; --syn-str: #e6db74; --syn-fn: #66d9ef;
    --syn-num: #ae81ff; --syn-com: #75715e;

    --ok: #a6e22e; --err: #f92672;
    --hl: rgba(255,255,255,.03); --shadow: rgba(0,0,0,.55); --grain-op: .05;
  }

  /* ── High Contrast. Pure black is deliberate here — it is the whole point
        of an accessibility theme, not the usual flat-black mistake. ───────── */
  :root[data-theme="contrast"] {
    color-scheme: dark;
    --bg: #000000; --editor: #000000; --panel: #000000; --raised: #0d0d0d;
    --line: #6b6b6b; --line-soft: #3d3d3d;
    --fg: #ffffff; --fg-dim: #d8d8d8; --fg-mute: #a6a6a6; --gutter: #8a8a8a;

    --accent: #6fc3df; --accent-dim: #4a8fa6;
    --accent-rgb: 111, 195, 223; --accent-ink: #000000;

    --syn-key: #c586c0; --syn-str: #ce9178; --syn-fn: #9cdcfe;
    --syn-num: #b5cea8; --syn-com: #7ca668;

    --ok: #89d185; --err: #f48771;
    --hl: rgba(255,255,255,.07); --shadow: rgba(0,0,0,.85); --grain-op: 0;
  }

  /* ══════════════════════════════════════════════════════════════════════════
     RESET & BASE
  ══════════════════════════════════════════════════════════════════════════ */
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  html {
    scroll-behavior: smooth;
    font-size: 16px;
    scroll-padding-top: calc(var(--titlebar) + 44px);
    background: var(--bg);
  }

  body {
    font-family: 'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
    background: var(--bg);
    color: var(--fg);
    font-variant-numeric: tabular-nums;
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
    overflow-x: hidden;
  }

  img, video, svg { max-width: 100%; display: block; }
  a { color: inherit; }
  button, input, textarea { font: inherit; color: inherit; }

  ::selection { background: rgba(var(--accent-rgb), .22); color: #fff; }

  /* VS Code style scrollbar */
  ::-webkit-scrollbar { width: 12px; height: 12px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb {
    background: color-mix(in oklab, var(--fg) 20%, var(--bg));
    border: 3px solid var(--bg); border-radius: 8px;
  }
  ::-webkit-scrollbar-thumb:hover { background: color-mix(in oklab, var(--fg) 34%, var(--bg)); }

  /* Keyboard focus — never removed, tinted to the accent */
  :focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
    border-radius: 2px;
  }

  .skip-link {
    position: absolute; left: 8px; top: -60px; z-index: var(--z-overlay);
    background: var(--accent); color: var(--accent-ink); padding: 10px 16px;
    font-size: 12px; font-weight: 700; border-radius: 4px;
    transition: top .18s cubic-bezier(.2,.7,.3,1);
  }
  .skip-link:focus { top: 8px; }

  /* Film grain — breaks the digital flatness */
  .grain {
    position: fixed; inset: 0; z-index: var(--z-noise);
    /* No mix-blend-mode: a fixed, full-viewport blended layer forces the whole
       page to re-composite on every scroll frame. Plain alpha costs nothing. */
    pointer-events: none; opacity: var(--grain-op);
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='160' height='160' filter='url(%23n)'/%3E%3C/svg%3E");
  }

  /* ══════════════════════════════════════════════════════════════════════════
     WINDOW CHROME — title bar / status bar
  ══════════════════════════════════════════════════════════════════════════ */
  .titlebar {
    position: fixed; top: 0; left: 0; right: 0; height: var(--titlebar);
    z-index: var(--z-chrome);
    display: flex; align-items: center; gap: 14px;
    padding: 0 12px;
    background: var(--panel);
    border-bottom: 1px solid var(--line);
    font-size: 11px; color: var(--fg-mute);
    user-select: none;
  }
  .titlebar-dots { display: flex; gap: 7px; flex-shrink: 0; }
  .dot { width: 11px; height: 11px; border-radius: 50%; transition: filter .18s; }
  .titlebar-dots:hover .dot { filter: brightness(1.35); }

  .titlebar-greet { color: var(--fg-mute); white-space: nowrap; }
  .titlebar-greet b { color: var(--fg-dim); font-weight: 500; }

  .titlebar-right { margin-left: auto; display: flex; align-items: center; gap: 16px; white-space: nowrap; }

  .availability { display: inline-flex; align-items: center; gap: 7px; color: var(--accent); }
  .pulse {
    width: 7px; height: 7px; border-radius: 50%; background: var(--accent); flex-shrink: 0;
    animation: pulse 2.4s cubic-bezier(.4,0,.6,1) infinite;
  }
  @keyframes pulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(var(--accent-rgb), .4); }
    50%      { box-shadow: 0 0 0 5px rgba(var(--accent-rgb), 0); }
  }

  .statusbar {
    position: fixed; bottom: 0; left: 0; right: 0; height: var(--statusbar);
    z-index: var(--z-chrome);
    display: flex; align-items: center; gap: 18px;
    padding: 0 12px;
    background: var(--panel);
    border-top: 1px solid var(--line);
    font-size: 10.5px; color: var(--fg-mute);
    user-select: none;
  }
  .statusbar-item { display: inline-flex; align-items: center; gap: 6px; transition: color .18s; text-decoration: none; }
  a.statusbar-item:hover { color: var(--accent); }
  .statusbar-right { margin-left: auto; display: flex; align-items: center; gap: 18px; }

  /* ══════════════════════════════════════════════════════════════════════════
     SHELL LAYOUT — sidebar | editor | outline
  ══════════════════════════════════════════════════════════════════════════ */
  .ide {
    display: grid;
    grid-template-columns: 268px minmax(0, 1fr) 190px;
    padding-top: var(--titlebar);
    padding-bottom: var(--statusbar);
    min-height: 100dvh;
    position: relative;
    z-index: var(--z-content);
  }

  .rail {
    position: sticky;
    top: var(--titlebar);
    height: calc(100dvh - var(--titlebar) - var(--statusbar));
    overflow-y: auto;
    background: var(--panel);
    z-index: var(--z-rail);
  }
  .rail::-webkit-scrollbar { width: 8px; }
  .rail-left  { border-right: 1px solid var(--line); padding: 26px 22px; }
  .rail-right { border-left: 1px solid var(--line);  padding: 26px 0 26px 20px; }

  .editor {
    background: var(--editor);
    position: relative;
    min-width: 0;
  }

  /* Line-number gutter — locked to --lh so numbers land on the rhythm */
  .gutter {
    position: absolute; top: 0; left: 0; bottom: 0; width: 46px;
    padding-top: 52px;
    text-align: right;
    font-size: 10.5px; line-height: var(--lh);
    color: var(--gutter);
    user-select: none; pointer-events: none;
    border-right: 1px solid var(--line-soft);
    overflow: hidden;
  }
  .gutter span { display: block; padding-right: 10px; }

  /* Current-line highlight — moved by transform only, never re-rendered */
  .line-highlight {
    position: absolute; top: 0; left: 0; right: 0; height: var(--lh);
    background: var(--hl);
    border-top:    1px solid var(--hl);
    border-bottom: 1px solid var(--hl);
    pointer-events: none; will-change: transform; z-index: 0;
  }

  .gutter { z-index: 1; }
  .editor-body { position: relative; z-index: 1; padding-left: 46px; }

  /* ══════════════════════════════════════════════════════════════════════════
     TAB BAR
  ══════════════════════════════════════════════════════════════════════════ */
  /* Strip = scrolling tabs + a pinned trailing control. The picker sits outside
     the overflow container so it never scrolls out of reach. */
  .tabstrip {
    position: sticky; top: var(--titlebar); z-index: var(--z-rail);
    display: flex; align-items: stretch;
    background: var(--panel);
    border-bottom: 1px solid var(--line);
  }

  .tabbar {
    display: flex; align-items: stretch;
    flex: 1; min-width: 0;
    overflow-x: auto;
    scrollbar-width: none;
  }
  .tabbar::-webkit-scrollbar { display: none; }

  /* ── Theme picker ───────────────────────────────────────────────────────── */
  .theme-picker {
    display: flex; align-items: center; gap: 8px;
    flex-shrink: 0; padding: 0 12px;
    border-left: 1px solid var(--line);
    color: var(--fg-mute); font-size: 11px;
  }

  .theme-picker select {
    appearance: none; -webkit-appearance: none;
    background-color: transparent;
    border: 1px solid transparent; border-radius: 4px;
    color: var(--fg-dim); font-size: 11px;
    padding: 5px 26px 5px 9px;
    cursor: pointer;
    transition: color .18s, background-color .18s, border-color .18s;
    /* Chevron drawn in CSS from currentColor, so it re-themes for free */
    background-image:
      linear-gradient(45deg,  transparent 50%, currentColor 50%),
      linear-gradient(135deg, currentColor 50%, transparent 50%);
    background-size: 4px 4px, 4px 4px;
    background-position: calc(100% - 13px) center, calc(100% - 9px) center;
    background-repeat: no-repeat;
  }
  .theme-picker select:hover {
    color: var(--fg);
    background-color: color-mix(in oklab, var(--panel) 90%, var(--fg));
    border-color: var(--line);
  }
  /* The popup itself is OS-drawn — color-scheme on :root makes it follow the theme */
  .theme-picker option { background: var(--panel); color: var(--fg); }

  .tab {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 0 16px; height: 36px; flex-shrink: 0;
    background: transparent; border: none; border-right: 1px solid var(--line);
    color: var(--fg-mute); font-size: 11.5px; cursor: pointer;
    position: relative;
    transition: background .18s, color .18s;
  }
  .tab::before {
    content: ''; position: absolute; inset: 0 0 auto 0; height: 1px;
    background: var(--accent);
    transform: scaleX(0); transform-origin: left;
    transition: transform .26s cubic-bezier(.2,.7,.3,1);
  }
  .tab:hover  { background: color-mix(in oklab, var(--panel) 93%, var(--fg)); color: var(--fg-dim); }
  .tab.active { background: var(--editor); color: var(--fg); }
  .tab.active::before { transform: scaleX(1); }
  .tab .dotfile { color: var(--syn-fn); }
  .tab.active .dotfile { color: var(--accent); }

  /* ══════════════════════════════════════════════════════════════════════════
     SIDEBAR (identity card)
  ══════════════════════════════════════════════════════════════════════════ */
  .avatar {
    width: 46px; height: 46px; border-radius: 10px; flex-shrink: 0;
    display: grid; place-items: center;
    background: linear-gradient(150deg,
      color-mix(in oklab, var(--raised) 82%, var(--fg)),
      var(--raised));
    border: 1px solid var(--line);
    font-family: 'Space Grotesk', sans-serif;
    font-size: 17px; font-weight: 700; color: var(--accent);
    letter-spacing: -.5px;
  }

  .side-name {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 16px; font-weight: 600; color: var(--fg); letter-spacing: -.2px;
  }
  .side-role { font-size: 10.5px; color: var(--fg-mute); margin-top: 3px; }

  .side-bio {
    font-size: 11.5px; line-height: 1.85; color: var(--fg-dim);
    margin: 22px 0; max-width: 34ch; text-wrap: pretty;
  }

  .side-meta { display: flex; flex-direction: column; gap: 11px; margin-bottom: 24px; }
  .side-meta-row {
    display: flex; align-items: center; gap: 10px;
    font-size: 11px; color: var(--fg-dim); text-decoration: none;
    transition: color .18s;
  }
  a.side-meta-row:hover { color: var(--accent); }
  .side-meta-row .ico { color: var(--fg-mute); flex-shrink: 0; display: flex; }
  a.side-meta-row:hover .ico { color: var(--accent); }

  .side-divider { height: 1px; background: var(--line); margin: 22px 0; }

  /* ══════════════════════════════════════════════════════════════════════════
     OUTLINE (right rail nav)
  ══════════════════════════════════════════════════════════════════════════ */
  .outline-title {
    font-size: 9.5px; letter-spacing: .16em; text-transform: uppercase;
    color: var(--fg-mute); padding: 0 20px 14px 0;
  }
  .outline-link {
    display: block; width: 100%; text-align: left;
    background: none; border: none; cursor: pointer;
    padding: 6px 0 6px 12px;
    font-size: 11px; color: var(--fg-mute);
    border-left: 2px solid transparent;
    transition: color .2s, border-color .2s, padding-left .2s;
  }
  .outline-link:hover  { color: var(--fg-dim); padding-left: 16px; }
  .outline-link.active { color: var(--accent); border-left-color: var(--accent); }

  /* ══════════════════════════════════════════════════════════════════════════
     SECTIONS & CODE-COMMENT HEADERS
  ══════════════════════════════════════════════════════════════════════════ */
  .sec { padding: 84px 44px; max-width: 1080px; }

  .sec-marker {
    font-size: 11px; color: var(--syn-com);
    margin-bottom: 26px; display: block;
  }
  .sec-marker b { color: var(--fg-mute); font-weight: 400; }

  h1, h2, h3 { font-family: 'Space Grotesk', sans-serif; font-weight: 600; }

  .display {
    font-size: clamp(38px, 6vw, 78px);
    line-height: .98;
    letter-spacing: -.035em;
    color: var(--fg);
    text-wrap: balance;
  }
  .display .ghost { color: var(--fg-mute); }

  .h2 {
    font-size: clamp(26px, 3.4vw, 46px);
    line-height: 1.04;
    letter-spacing: -.03em;
    text-wrap: balance;
  }
  .h2 .ghost { color: var(--fg-mute); }

  .h3 { font-size: 15px; font-weight: 600; letter-spacing: -.01em; color: var(--fg); }

  .prose {
    font-size: 12.5px; line-height: 1.95; color: var(--fg-dim);
    max-width: 62ch; text-wrap: pretty;
  }

  /* Inline syntax tokens used inside prose */
  .t-key { color: var(--syn-key); }
  .t-str { color: var(--syn-str); }
  .t-fn  { color: var(--syn-fn);  }
  .t-num { color: var(--syn-num); }
  .t-com { color: var(--syn-com); }
  .t-hi  { color: var(--accent); }
  .t-mark { color: var(--fg); background: rgba(var(--accent-rgb), .10); padding: 1px 4px; border-radius: 3px; }

  /* Giant watermark type (the "SINCE ____" moment) */
  .watermark {
    font-family: 'Space Grotesk', sans-serif;
    font-size: clamp(64px, 14vw, 190px);
    font-weight: 700; letter-spacing: -.05em; line-height: .85;
    color: transparent;
    -webkit-text-stroke: 1px color-mix(in oklab, var(--fg) 13%, var(--editor));
    user-select: none; pointer-events: none;
    padding: 40px 44px;
  }
  .watermark .filled { color: color-mix(in oklab, var(--fg) 8%, var(--editor)); -webkit-text-stroke: 0; }

  /* ══════════════════════════════════════════════════════════════════════════
     SURFACES
  ══════════════════════════════════════════════════════════════════════════ */
  .panel {
    background: var(--raised);
    border: 1px solid var(--line);
    border-radius: 6px;
    transition: border-color .22s, transform .22s cubic-bezier(.2,.7,.3,1), box-shadow .22s;
  }
  .panel:hover {
    border-color: color-mix(in oklab, var(--line) 70%, var(--fg));
    transform: translateY(-2px);
    box-shadow: 0 12px 34px var(--shadow);   /* tinted to the backdrop, not pure black */
  }

  /* Project cards — image-led, VS Code "open editor" feel */
  .work-card {
    display: block; text-decoration: none;
    border: 1px solid var(--line); border-radius: 4px;
    background: var(--raised);
    overflow: hidden;
    transition: border-color .22s, transform .3s cubic-bezier(.2,.7,.3,1);
  }
  .work-card:hover { border-color: var(--accent-dim); transform: translateY(-3px); }

  .work-head {
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
    padding: 11px 14px; border-bottom: 1px solid var(--line);
    font-size: 11px; color: var(--fg-dim);
  }
  .work-card:hover .work-head { color: var(--fg); }
  .work-arrow { transition: transform .3s cubic-bezier(.2,.7,.3,1); flex-shrink: 0; }
  .work-card:hover .work-arrow { transform: translate(3px, -3px); }

  .work-visual {
    aspect-ratio: 4 / 3;
    background: color-mix(in oklab, var(--bg) 70%, var(--editor));
    display: grid; place-items: center;
    position: relative; overflow: hidden;
  }
  .work-visual::after {
    content: ''; position: absolute; inset: 0;
    background: radial-gradient(120% 90% at 30% 15%, rgba(var(--accent-rgb), .07), transparent 62%);
    opacity: 0; transition: opacity .34s;
  }
  .work-card:hover .work-visual::after { opacity: 1; }
  .work-glyph {
    font-size: clamp(40px, 7vw, 64px);
    filter: grayscale(1) contrast(.85) brightness(1.15);
    opacity: .5;
    transition: filter .4s, opacity .4s, transform .5s cubic-bezier(.2,.7,.3,1);
  }
  .work-card:hover .work-glyph { filter: grayscale(0) contrast(1); opacity: 1; transform: scale(1.06); }

  .work-body { padding: 16px 14px 18px; }

  /* Chips */
  .chip {
    display: inline-block;
    font-size: 10px; color: var(--fg-dim);
    padding: 3px 8px;
    border: 1px solid var(--line);
    border-radius: 3px;
    background: color-mix(in oklab, var(--bg) 70%, var(--editor));
  }

  /* Skill rows — table-like, left key + value tokens */
  .skill-row {
    display: grid; grid-template-columns: 150px minmax(0, 1fr);
    gap: 20px; align-items: start;
    padding: 15px 0;
    border-bottom: 1px solid var(--line-soft);
  }
  .skill-row:last-child { border-bottom: none; }
  .skill-key { font-size: 11px; color: var(--syn-key); }
  .skill-vals { display: flex; flex-wrap: wrap; gap: 7px; }

  /* Timeline / list rows */
  .row-item {
    display: grid; grid-template-columns: 92px minmax(0, 1fr);
    gap: 22px; align-items: baseline;
    padding: 14px 0;
    border-bottom: 1px solid var(--line-soft);
    transition: border-color .2s;
  }
  .row-item:hover { border-bottom-color: var(--line); }
  .row-year { font-size: 11px; color: var(--syn-num); }

  /* Collapsed extra list items */
  .more > summary {
    display: inline-flex; align-items: center; gap: 7px;
    margin-top: 12px; cursor: pointer;
    font-size: 11px; color: var(--fg-mute);
    list-style: none;
  }
  .more > summary::-webkit-details-marker { display: none; }
  .more > summary::before { content: "+"; color: var(--accent); }
  .more[open] > summary::before { content: "−"; }
  .more > summary:hover { color: var(--fg); }

  /* ══════════════════════════════════════════════════════════════════════════
     BUTTONS
  ══════════════════════════════════════════════════════════════════════════ */
  .btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    padding: 11px 20px;
    font-size: 11.5px; font-weight: 500;
    border-radius: 4px; cursor: pointer;
    text-decoration: none; white-space: nowrap;
    border: 1px solid var(--line);
    background: var(--raised); color: var(--fg);
    transition: background .18s, border-color .18s, color .18s, transform .12s;
  }
  .btn:hover  {
    border-color: color-mix(in oklab, var(--line) 62%, var(--fg));
    background: color-mix(in oklab, var(--raised) 90%, var(--fg));
  }
  .btn:active { transform: translateY(1px); }

  .btn-accent { background: var(--accent); border-color: var(--accent); color: var(--accent-ink); font-weight: 600; }
  .btn-accent:hover { opacity: .86; }   /* works in every theme, light accent or dark */
  .btn:disabled { opacity: .45; cursor: not-allowed; transform: none; }

  .btn-ghost { background: transparent; border-color: transparent; color: var(--fg-dim); padding-left: 0; padding-right: 0; }
  .btn-ghost:hover { background: transparent; color: var(--accent); }

  /* ══════════════════════════════════════════════════════════════════════════
     FORM
  ══════════════════════════════════════════════════════════════════════════ */
  .field { display: flex; flex-direction: column; gap: 7px; }
  .field-label { font-size: 10px; letter-spacing: .1em; text-transform: uppercase; color: var(--fg-mute); }

  .input {
    width: 100%;
    background: transparent;
    border: none; border-bottom: 1px solid var(--line);
    padding: 10px 2px;
    font-size: 12.5px; color: var(--fg);
    outline: none;
    transition: border-color .22s;
  }
  .input::placeholder { color: color-mix(in oklab, var(--fg-mute) 62%, var(--editor)); }
  .input:hover { border-bottom-color: color-mix(in oklab, var(--line) 70%, var(--fg)); }
  .input:focus { border-bottom-color: var(--accent); outline: none; }
  .input:focus-visible { outline: none; }
  .input[aria-invalid="true"] { border-bottom-color: var(--err); }
  textarea.input { resize: vertical; min-height: 92px; line-height: 1.7; }

  .field-error { font-size: 10.5px; color: var(--err); }

  .alert {
    display: flex; align-items: flex-start; gap: 10px;
    padding: 11px 13px; border-radius: 4px;
    font-size: 11px; line-height: 1.6;
    background: color-mix(in oklab, var(--err) 9%, transparent);
    border: 1px solid color-mix(in oklab, var(--err) 30%, transparent);
    color: color-mix(in oklab, var(--err) 78%, var(--fg));
  }

  /* ══════════════════════════════════════════════════════════════════════════
     ANIMATION
  ══════════════════════════════════════════════════════════════════════════ */
  @keyframes rise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }

  /* Staggered entry — GPU-only properties */
  .reveal { opacity: 0; }
  .reveal.in { animation: rise .62s cubic-bezier(.2,.7,.3,1) forwards; }
  .reveal.in.d1 { animation-delay: .07s; }
  .reveal.in.d2 { animation-delay: .14s; }
  .reveal.in.d3 { animation-delay: .21s; }
  .reveal.in.d4 { animation-delay: .28s; }
  .reveal.in.d5 { animation-delay: .35s; }

  /* Blinking editor caret */
  .caret {
    display: inline-block; width: 2px; height: .92em;
    background: var(--accent); vertical-align: text-bottom;
    margin-left: 3px;
    animation: blink 1.06s steps(1) infinite;
  }
  @keyframes blink { 0%, 49% { opacity: 1; } 50%, 100% { opacity: 0; } }

  /* Skeleton shimmer for the sending state */
  @keyframes shimmer { from { background-position: -180% 0; } to { background-position: 180% 0; } }
  .skeleton {
    height: 9px; border-radius: 3px;
    background: linear-gradient(90deg,
      color-mix(in oklab, var(--raised) 92%, var(--fg)) 25%,
      color-mix(in oklab, var(--raised) 78%, var(--fg)) 50%,
      color-mix(in oklab, var(--raised) 92%, var(--fg)) 75%);
    background-size: 180% 100%;
    animation: shimmer 1.3s linear infinite;
  }

  @keyframes spin { to { transform: rotate(360deg); } }
  .spin { animation: spin .9s linear infinite; display: inline-flex; }

  @media (prefers-reduced-motion: reduce) {
    html { scroll-behavior: auto; }
    *, *::before, *::after {
      animation-duration: .001ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: .001ms !important;
    }
    .reveal { opacity: 1; }
  }

  /* ══════════════════════════════════════════════════════════════════════════
     GRIDS
  ══════════════════════════════════════════════════════════════════════════ */
  .grid-work    { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; }
  .grid-cards   { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
  .grid-split   { display: grid; grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr); gap: 52px; align-items: start; }
  .grid-contact { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.25fr); gap: 52px; align-items: start; }

  /* ══════════════════════════════════════════════════════════════════════════
     RESPONSIVE
     1160 drop outline · 900 drop sidebar+gutter · 620 phone
  ══════════════════════════════════════════════════════════════════════════ */
  @media (max-width: 1160px) {
    .ide { grid-template-columns: 248px minmax(0, 1fr); }
    .rail-right { display: none; }
  }

  @media (max-width: 900px) {
    .ide { grid-template-columns: minmax(0, 1fr); }
    .rail-left {
      position: static; height: auto;
      border-right: none; border-bottom: 1px solid var(--line);
      padding: 24px 20px;
    }
    .gutter { display: none; }
    .editor-body { padding-left: 0; }
    .sec { padding: 60px 20px; }
    .grid-split, .grid-contact { grid-template-columns: minmax(0, 1fr); gap: 36px; }
    .watermark { padding: 28px 20px; }
  }

  @media (max-width: 620px) {
    .sec { padding: 48px 16px; }
    .grid-work, .grid-cards { grid-template-columns: minmax(0, 1fr); }
    .skill-row { grid-template-columns: minmax(0, 1fr); gap: 10px; }
    .row-item  { grid-template-columns: minmax(0, 1fr); gap: 6px; }
    .titlebar-greet { display: none; }
    .theme-picker { padding: 0 4px; }
    .theme-picker .picker-label { display: none; }
    .statusbar { gap: 12px; font-size: 9.5px; }
    .btn-block-xs { width: 100%; }
  }

  @media (max-width: 420px) {
    .statusbar-right .hide-xs { display: none; }
  }
`;
